import React, { ReactElement } from 'react';
import cn from 'classnames';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import TableHead from '@material-ui/core/TableHead';
import {
  Box,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import defaultLocales from 'globals/locales/default';
import { isLastElement } from 'globals/utils';
import { FsComponentSize, InvitationStatus } from 'globals/types';
import { grey } from 'globals/colors';
import FsStatusLabel from 'ui-elements/common-elements/FsStatusLabel';
import FsDate from 'ui-elements/common-elements/FsDate';
import FsAvatarWithName from 'ui-elements/common-elements/FsAvatarWithName';
import FsIconButton from 'ui-elements/buttons/FsIconButton';
import { getColor } from './FsInvitationsTable.style';
import {
  BodyLabel,
  Cell,
  Email,
  HeadCell,
  HeadLabel,
  Name,
  Reference,
  Row,
} from './FsInvitationsTable.style-elements';

export interface FsInvitationsTableSingleRowProps {
  id: string | number;
  name: string | ReactElement;
  email: string;
  code: string;
  statusType: InvitationStatus;
  date: number;
  invitedBy?: string;
}

export interface FsInvitationsTableProps {
  rows?: FsInvitationsTableSingleRowProps[];
  isLongVersion?: boolean;
  headLabels?: string[];
}

const FsInvitationsTable: React.FC<FsInvitationsTableProps> = ({
  rows = [],
  headLabels = [],
  isLongVersion = false,
}) => {
  const theme = useTheme();
  const isUpLg = useMediaQuery(theme.breakpoints.up('lg'), {
    noSsr: true,
  });
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement>();
  const [currentId, setCurrentId] = React.useState<number | string>();
  const open = Boolean(anchorEl);
  const activeHeadLabels = isUpLg
    ? headLabels
    : headLabels
        .filter(
          (label) =>
            label !== defaultLocales.invitedBy &&
            label !== defaultLocales.opened
        )
        .map((label) =>
          label === defaultLocales.send ? defaultLocales.activity : label
        );
  const handleIconButtonClick = (
    event: React.MouseEvent<HTMLElement>,
    id: number | string
  ): void => {
    setAnchorEl(event.currentTarget);
    setCurrentId(id);
  };

  const handleClose = (): void => {
    setAnchorEl(undefined);
  };

  const handleMenuItemClick = (): void => {
    handleClose();
  };

  return (
    <TableContainer>
      <Table>
        {activeHeadLabels.length ? (
          <TableHead>
            <TableRow data-test="table-head-row">
              {activeHeadLabels.map((label) => (
                <HeadCell
                  key={label}
                  withIconOnly={isLongVersion && label === ''}
                >
                  <HeadLabel>{label}</HeadLabel>
                </HeadCell>
              ))}
            </TableRow>
          </TableHead>
        ) : null}
        <TableBody>
          {rows.map(
            ({ id, name, statusType, code, date, email, invitedBy }, idx) => (
              <Row
                key={id}
                className={cn({ isLast: isLastElement(idx, rows?.length) })}
              >
                <Cell component="th">
                  <Name>{name}</Name>
                  <Email>{email}</Email>
                  {!isUpLg && isLongVersion && (
                    <Box marginTop={1}>
                      <BodyLabel>{headLabels[4]}</BodyLabel>
                      <FsAvatarWithName size={FsComponentSize.S}>
                        {name}
                      </FsAvatarWithName>
                    </Box>
                  )}
                </Cell>
                <Cell>
                  <Reference>{code}</Reference>
                </Cell>
                <Cell>
                  {(isLongVersion && isUpLg) || !isLongVersion ? (
                    <FsDate date={date} withYear />
                  ) : (
                    <>
                      <BodyLabel>{headLabels[2]}</BodyLabel>
                      <FsDate date={date} withYear />
                      <Box marginTop={1}>
                        <BodyLabel>{headLabels[3]}</BodyLabel>
                        <FsDate date={date} showDifference />
                      </Box>
                    </>
                  )}
                </Cell>
                {isLongVersion && isUpLg && (
                  <Cell>
                    <FsDate date={date} showDifference />
                  </Cell>
                )}
                {isLongVersion && isUpLg && (
                  <Cell width="20%">
                    {invitedBy ? (
                      <FsAvatarWithName>{name}</FsAvatarWithName>
                    ) : (
                      <FsAvatarWithName color={grey.V3}>
                        {defaultLocales.apiGenerated}
                      </FsAvatarWithName>
                    )}
                  </Cell>
                )}
                <Cell>
                  <FsStatusLabel bgColor={getColor(statusType)}>
                    {defaultLocales.enums[statusType]}
                  </FsStatusLabel>
                </Cell>
                {isLongVersion && (
                  <Cell withIconOnly>
                    <FsIconButton
                      size="small"
                      onClick={(e) => handleIconButtonClick(e, id)}
                    >
                      <MoreVertIcon />
                    </FsIconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={open && currentId === id}
                      onClose={handleClose}
                    >
                      <MenuItem onClick={handleMenuItemClick}>
                        <span>{defaultLocales.revoke}</span>
                      </MenuItem>
                    </Menu>
                  </Cell>
                )}
              </Row>
            )
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FsInvitationsTable;
